import React, { useState, useEffect, useContext } from 'react';
import { Box, Paper, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { Refresh as RefreshIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { Form990PDFGenerator } from '../../../../../services/form990/Form990PDFGenerator';
import { Form990Data } from '../../types/form990.types';
import { useAuth } from '../../../../../../contexts/AuthContext';
import { DatabaseContext } from '../../../../../contexts/DatabaseContext';
import { ServiceContext } from '../../../../../contexts/ServiceContext';

interface PreviewProps {
  data: Form990Data | null;
  onNext: () => void;
  isLoading: boolean;
  validation?: { message: string }[];
  onUpdate: () => void;
}

export const PreviewDisplay: React.FC<PreviewProps> = ({
  data,
  onNext,
  isLoading,
  validation = [],
  onUpdate
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localLoading, setLocalLoading] = useState(false);
  const services = useContext(ServiceContext);

  const hasValidationIssues = validation && validation.length > 0;

  const handleGeneratePreview = async () => {
    if (!services || !data) return;
    
    try {
      setLocalLoading(true);
      setError(null);
      
      // Create a data object that matches what the service expects
      // This is separate from the Form990Data type
      const serviceData = {
        responses: {
          organization_name: data.data.header.name,
          ein: data.data.header.ein,
          tax_year_start: new Date(data.year, 0, 1).toISOString(), // Jan 1 of the form year
          tax_year_end: new Date(data.year, 11, 31).toISOString(), // Dec 31 of the form year
          address_street: data.data.header.address.street,
          address_city: data.data.header.address.city,
          address_state: data.data.header.address.state,
          address_zip: data.data.header.address.zip,
          website: data.data.header.website,
          year_formed: data.data.header.yearFormed?.toString() || '',
          total_revenue: '0',
          program_service_revenue: '0',
          investment_income: '0',
          total_expenses: '0',
          program_service_expenses: '0',
          management_expenses: '0',
        }
      };
      
      // Use type assertion to tell TypeScript this is the correct type
      // for the service method
      const pdfBytes = await services.form990Service.generatePreview(serviceData as any);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      console.error('Preview generation failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate preview');
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" gutterBottom>
        Form 990 Preview & Review
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        Generate and review your Form 990 before signing.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Button
          variant="contained"
          onClick={handleGeneratePreview}
          disabled={isLoading || localLoading}
          startIcon={<RefreshIcon />}
        >
          Generate Form 990
        </Button>

        {pdfUrl && !hasValidationIssues && (
          <Button
            variant="outlined"
            onClick={onNext}
            disabled={isLoading || localLoading}
            endIcon={<ArrowForwardIcon />}
          >
            Continue to Sign
          </Button>
        )}
      </Box>

      {hasValidationIssues && (
        <Box sx={{ mb: 3 }}>
          <Typography color="error">
            Please address the following issues:
          </Typography>
          <ul>
            {validation.map((issue, index) => (
              <li key={index}>{issue.message}</li>
            ))}
          </ul>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {(isLoading || localLoading) && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {pdfUrl && (
        <Paper elevation={2} sx={{ mb: 3 }}>
          <Box sx={{ 
            height: '700px',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            bgcolor: 'background.paper',
            overflow: 'hidden'
          }}>
            <embed
              src={pdfUrl}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
}; 